import "./site/error-beacon";
import { Vortex, VortexResult, RUN_MS } from "./games/vortex/vortex";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.vortex_pb";
const HANDLE_KEY = "aether.handle";

flushFailedSubmits("vortex");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startVortexRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/vortex", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishVortexRun(
  runId: string, handle: string,
  flipEvents: VortexResult["flipEvents"],
  fallEvent: VortexResult["fallEvent"],
  endT: number,
) {
  const url = `/api/runs/vortex/${runId}`;
  const body = { handle, flips: flipEvents, fall: fallEvent, end_t: endT };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("vortex", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("vortex", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudDistance = document.getElementById("hud-distance")!;
const hudTime = document.getElementById("hud-time")!;
const hudCombo = document.getElementById("hud-combo")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Vortex | null = null;
let activeRunId: string | null = null;
let pendingResult: VortexResult | null = null;

async function startRun() {
  if (game) game.dispose();
  const runStart = await startVortexRun();
  activeRunId = runStart?.run_id ?? null;
  const seed = runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Vortex(canvas, {
    onHud: (runMs, distance, _wall, combo, scoreEstimate) => {
      hudDistance.textContent = Math.floor(distance).toString();
      hudTime.textContent = ((RUN_MS - runMs) / 1000).toFixed(1);
      hudCombo.textContent = combo > 0 ? `${combo}×` : "—";
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async (result) => {
      hud.dataset.state = "over";
      msgTitle.textContent = result.fell ? "Fall" : "Run complete";
      const ringsNote = result.rings_collected > 0 ? ` · ${result.rings_collected} rings` : "";
      msgBody.textContent = `${result.scoreEstimate.toLocaleString()} pts · ${Math.floor(result.distance_m)}m · ${result.flips} flips · peak ${result.peak_combo}×${ringsNote}`;
      btnStart.textContent = "Try again";

      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (result.scoreEstimate > pb) localStorage.setItem(PB_KEY, String(result.scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "vortex", score: result.scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishVortexRun(activeRunId, handle, result.flipEvents, result.fallEvent, result.endT);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingResult = result;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Vortex";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingResult) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishVortexRun(activeRunId, value, pendingResult.flipEvents, pendingResult.fallEvent, pendingResult.endT);
  pendingResult = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
